import {z} from "zod";

/** Sprout Plus: one service level, sold by the App Store on iPhone and by Stripe on the web. */
export const sproutPlus = {
  name: "Sprout Plus",
  trialDays: 3,
  currency: "USD",
  plans: {
    yearly: {productId: "com.mindcrafters.chatwithyourfinance.plus.yearly", price: 99.99, interval: "year"},
    weekly: {productId: "com.mindcrafters.chatwithyourfinance.plus.weekly", price: 9.99, interval: "week"}
  }
} as const;

export const billingPlans = ["yearly", "weekly"] as const;
export type BillingPlan = (typeof billingPlans)[number];
export const billingProviders = ["apple", "stripe", "comp"] as const;
export type BillingProvider = (typeof billingProviders)[number];

export function planForProduct(productId: string | null | undefined): BillingPlan | null {
  return billingPlans.find((plan) => sproutPlus.plans[plan].productId === productId) ?? null;
}

/** Every App Store product that grants Sprout Plus, including remotely configured offers, shares this prefix. */
export function isPlusProduct(productId: string | null | undefined): boolean {
  return typeof productId === "string" && /^com\.mindcrafters\.chatwithyourfinance\.plus\.[a-z0-9_.]+$/.test(productId);
}

/**
 * The server's verdict on paid access. Every client reads it; none decides it. `billingIssue` marks a web
 * subscription whose last charge was declined and that Stripe is still retrying; a new card in the billing portal restores it.
 */
export const entitlementSchema = z.discriminatedUnion("active", [z.object({active: z.literal(false), billingIssue: z.boolean().optional()}), z.object({active: z.literal(true), provider: z.enum(billingProviders), productId: z.string().nullable(), expiresAt: z.string().datetime(), trial: z.boolean()})]);
export type Entitlement = z.infer<typeof entitlementSchema>;
export interface EntitlementResponse {
  data: Entitlement;
}

/** A web plan priced by Stripe, shaped like a StoreKit product so one paywall renders both. */
export const billingProductSchema = z.object({
  id: z.string(),
  plan: z.enum(billingPlans),
  displayPrice: z.string().min(1),
  price: z.number().positive(),
  currency: z.string().length(3),
  trialDays: z.number().int().positive().nullable()
});
export type BillingProduct = z.infer<typeof billingProductSchema>;
export interface BillingPlansResponse {
  data: BillingProduct[];
}

/** StoreKit's signed transaction, plus its renewal info when a billing grace period extends access. */
export const appleSyncSchema = z.object({signedTransaction: z.string().min(1).max(24_000), signedRenewalInfo: z.string().min(1).max(24_000).optional()});
export type AppleSync = z.infer<typeof appleSyncSchema>;
export const checkoutInputSchema = z.object({plan: z.enum(billingPlans)});
export type CheckoutInput = z.infer<typeof checkoutInputSchema>;
/** The Checkout Session Stripe names in the success URL, confirmed on return so access never waits for a webhook. */
export const checkoutConfirmSchema = z.object({session: z.string().regex(/^cs_[A-Za-z0-9_]{1,250}$/)});
export type CheckoutConfirm = z.infer<typeof checkoutConfirmSchema>;
/**
 * The start of the subscription a checkout created. The server captures it in PostHog under `id`; the browser
 * repeats it to ad and web destinations under the same ID, so each platform counts it once.
 */
export interface CheckoutStart {
  event: "trial_started" | "subscription_purchased";
  id: string;
}
export interface CheckoutConfirmResponse {
  data: Entitlement;
  /** Null until the subscription grants access. */
  start: CheckoutStart | null;
}
export interface BillingRedirectResponse {
  data: {url: string};
}

/** The API's 402 error code when an action needs Sprout Plus. */
export const subscriptionRequiredCode = "SUBSCRIPTION_REQUIRED";
